
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Stethoscope, Zap, Microscope, Droplets } from "lucide-react";
import { Link } from "react-router-dom";

const SignatureTreatmentsGrid = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const treatments = [
    {
      title: "Botox for TMJ",
      description: "Reduce jaw tension and clenching while improving facial balance. One of our most powerful wellness treatments.",
      icon: Stethoscope,
      link: "/treatments/botox"
    },
    {
      title: "Laser Facial",
      description: "Advanced light therapy for skin rejuvenation, targeting fine lines and improving texture.",
      icon: Zap,
      link: "/treatments/laser-facial"
    },
    {
      title: "RF Microneedling",
      description: "Radiofrequency technology combined with microneedling for dramatic skin tightening.",
      icon: Microscope,
      link: "/treatments/rf-microneedling"
    },
    {
      title: "PRF Treatment",
      description: "Platelet-rich fibrin therapy for natural skin regeneration and healing.",
      icon: Droplets,
      link: "/treatments/prf"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % treatments.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + treatments.length) % treatments.length);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-juv-deep-chestnut mb-6">
            Signature Treatments
          </h2>
          <p className="text-lg md:text-xl text-juv-deep-chestnut/70 leading-relaxed max-w-3xl mx-auto">
            Advanced treatments designed to enhance your natural beauty and wellness
          </p>
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {treatments.map((treatment, index) => (
                <div key={index} className="w-full flex-shrink-0 px-4">
                  <Card className="h-full shadow-luxury hover:shadow-editorial transition-all duration-300 border-juv-rose-clay/20 bg-white">
                    <div className="aspect-video bg-juv-soft-blush rounded-t-lg flex items-center justify-center">
                      <treatment.icon className="w-16 h-16 text-juv-terracotta" />
                    </div>
                    <CardHeader className="bg-white">
                      <CardTitle className="text-xl font-serif text-juv-deep-chestnut">
                        {treatment.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 bg-white">
                      <CardDescription className="text-juv-deep-chestnut/70 leading-relaxed">
                        {treatment.description}
                      </CardDescription>
                      <Button asChild className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut text-white">
                        <Link to={treatment.link}>Learn More</Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
          
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-juv-rose-clay text-white rounded-full flex items-center justify-center shadow-lg hover:bg-juv-terracotta transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-juv-rose-clay text-white rounded-full flex items-center justify-center shadow-lg hover:bg-juv-terracotta transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {treatments.map((treatment, index) => (
            <Card key={index} className="group hover:scale-105 transition-all duration-300 shadow-luxury hover:shadow-editorial border-juv-rose-clay/20 bg-white">
              <div className="aspect-video bg-juv-soft-blush rounded-t-lg flex items-center justify-center">
                <treatment.icon className="w-16 h-16 text-juv-terracotta group-hover:text-juv-deep-chestnut transition-colors duration-300" />
              </div>
              <CardHeader className="bg-white">
                <CardTitle className="text-xl font-serif text-juv-deep-chestnut group-hover:text-juv-terracotta transition-colors">
                  {treatment.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 bg-white">
                <CardDescription className="text-juv-deep-chestnut/70 leading-relaxed line-clamp-3">
                  {treatment.description}
                </CardDescription>
                <Button asChild className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut text-white">
                  <Link to={treatment.link}>Learn More</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <Button asChild size="lg" className="bg-juv-deep-chestnut hover:bg-juv-terracotta text-white px-8 py-4 rounded-full font-semibold">
            <Link to="/treatments">View All Services</Link>
          </Button>
        </div>
        
        {/* Quiz CTA */}
        <div className="text-center mt-8">
          <Button asChild size="lg" className="bg-juv-rose-clay hover:bg-juv-terracotta text-white px-8 py-4 rounded-full font-semibold">
            <Link to="/quiz">Take the Quiz to Get Matched</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SignatureTreatmentsGrid;
